import axios from "axios";
import config from "../../../config";
import type {
  TemplatesResponse,
  TemplateInformation,
  ValidCharactersResponse,
  AvailableValidCharactersResponse,
  ValidCharacterItem,
  ValueExpressionTestRequest,
  ValueExpressionTestResponse,
  ValueExpressionRequest,
  ValueExpressionsResponse,
  ReplacementCharactersResponse,
  ReplacementCharacterItem,
  RoutinesResponse,
  RoutineItem,
  AvailableRoutinesResponse,
  ValidValuesResponse,
  ValidValueItem,
  ReplacementValuesResponse,
  ReplacementValueItem,
  TemplateApisResponse,
  TemplateApiItem,
  AvailableApisResponse,
  EntitiesResponse,
  TemplateAssignmentResponse,
  TemplateAssignmentRequest,
  TemplateTestResponse,
} from "../types/api-types";

const apiUrl = config.api;

export const getTemplates = async (): Promise<TemplatesResponse> => {
  const response = await axios.get<TemplatesResponse>(
    `${apiUrl}/cleaning/templates`,
  );
  return response.data;
};

export const updateTemplate = async (
  template: TemplateInformation,
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/templates`, template);
};

export const getValidCharacters = async (
  templateId: number,
): Promise<ValidCharactersResponse> => {
  const response = await axios.get<ValidCharactersResponse>(
    `${apiUrl}/cleaning/template/valid-characters/${templateId}`,
  );
  return response.data;
};

export const getAllAvailableValidCharacters =
  async (): Promise<AvailableValidCharactersResponse> => {
    const response = await axios.get<AvailableValidCharactersResponse>(
      `${apiUrl}/cleaning/template/valid-characters`,
    );
    return response.data;
  };

export const saveTemplateValidCharacters = async (
  characters: ValidCharacterItem[],
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/template/valid-characters`, characters);
};

export const deleteTemplateValidCharacters = async (
  character: ValidCharacterItem,
): Promise<void> => {
  await axios.delete(`${apiUrl}/cleaning/template/valid-characters`, {
    data: character,
  });
};

export const getValueExpressions = async (
  templateId: number,
): Promise<ValueExpressionsResponse> => {
  const response = await axios.get<ValueExpressionsResponse>(
    `${apiUrl}/cleaning/template/value-expressions/${templateId}`,
  );
  return response.data;
};

export const testValueExpression = async (
  request: ValueExpressionTestRequest,
): Promise<ValueExpressionTestResponse> => {
  const response = await axios.post<ValueExpressionTestResponse>(
    `${apiUrl}/cleaning/template/value-expressions/test`,
    request,
  );
  return response.data;
};

export const saveValueExpression = async (
  request: ValueExpressionRequest,
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/template/value-expressions`, request);
};

export const updateValueExpression = async (
  request: ValueExpressionRequest,
): Promise<void> => {
  await axios.put(`${apiUrl}/cleaning/template/value-expressions`, request);
};

export const deleteValueExpression = async (
  request: ValueExpressionRequest,
): Promise<void> => {
  await axios.delete(`${apiUrl}/cleaning/template/value-expressions`, {
    data: request,
  });
};

export const getReplacementCharacters = async (
  templateId: number,
): Promise<ReplacementCharactersResponse> => {
  const response = await axios.get<ReplacementCharactersResponse>(
    `${apiUrl}/cleaning/template/replacement-characters/${templateId}`,
  );
  return response.data;
};

export const deleteReplacementCharacter = async (id: number): Promise<void> => {
  await axios.delete(
    `${apiUrl}/cleaning/template/replacement-characters/${id}`,
  );
};

export const saveReplacementCharacter = async (
  request: ReplacementCharacterItem,
): Promise<void> => {
  await axios.post(
    `${apiUrl}/cleaning/template/replacement-characters`,
    request,
  );
};

export const updateReplacementCharacter = async (
  request: ReplacementCharacterItem,
): Promise<void> => {
  await axios.put(
    `${apiUrl}/cleaning/template/replacement-characters`,
    request,
  );
};

// Routines
export const getRoutines = async (
  templateId: number,
): Promise<RoutinesResponse> => {
  const response = await axios.get<RoutinesResponse>(
    `${apiUrl}/cleaning/template/routines/${templateId}`,
  );
  return response.data;
};

export const getAvailableRoutines =
  async (): Promise<AvailableRoutinesResponse> => {
    const response = await axios.get<AvailableRoutinesResponse>(
      `${apiUrl}/cleaning/template/routines/enabled`,
    );
    return response.data;
  };

export const saveRoutine = async (request: RoutineItem): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/template/routines`, request);
};

export const deleteRoutine = async (id: number): Promise<void> => {
  await axios.delete(`${apiUrl}/cleaning/template/routines/${id}`);
};

// Valid Values
export const getValidValues = async (
  templateId: number,
): Promise<ValidValuesResponse> => {
  const response = await axios.get<ValidValuesResponse>(
    `${apiUrl}/cleaning/template/valid-values/${templateId}`,
  );
  return response.data;
};

export const saveValidValue = async (
  request: ValidValueItem,
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/template/valid-values`, request);
};

export const updateValidValue = async (
  request: ValidValueItem,
): Promise<void> => {
  await axios.put(`${apiUrl}/cleaning/template/valid-values`, request);
};

export const deleteValidValue = async (id: number): Promise<void> => {
  await axios.delete(`${apiUrl}/cleaning/template/valid-values/${id}`);
};

// Replacement Values
export const getReplacementValues = async (
  templateId: number,
): Promise<ReplacementValuesResponse> => {
  const response = await axios.get<ReplacementValuesResponse>(
    `${apiUrl}/cleaning/template/replacement-values/${templateId}`,
  );
  return response.data;
};

export const saveReplacementValue = async (
  request: ReplacementValueItem,
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/template/replacement-values`, request);
};

export const updateReplacementValue = async (
  request: ReplacementValueItem,
): Promise<void> => {
  await axios.put(`${apiUrl}/cleaning/template/replacement-values`, request);
};

export const deleteReplacementValue = async (id: number): Promise<void> => {
  await axios.delete(`${apiUrl}/cleaning/template/replacement-values/${id}`);
};

// Template APIs
export const getTemplateApis = async (
  templateId: number,
): Promise<TemplateApisResponse> => {
  const response = await axios.get<TemplateApisResponse>(
    `${apiUrl}/cleaning/template/apis/${templateId}`,
  );
  return response.data;
};

export const getAvailableApis = async (): Promise<AvailableApisResponse> => {
  const response = await axios.get<AvailableApisResponse>(
    `${apiUrl}/cleaning/template/apis`,
  );
  return response.data;
};

export const saveTemplateApi = async (
  request: TemplateApiItem,
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/template/apis`, request);
};

export const deleteTemplateApi = async (
  request: TemplateApiItem,
): Promise<void> => {
  await axios.delete(`${apiUrl}/cleaning/template/apis`, { data: request });
};

// Entities
export const getEntities = async (): Promise<EntitiesResponse> => {
  const response = await axios.get<EntitiesResponse>(`${apiUrl}/entity-types`);
  return response.data;
};

// Template Assignment
export const getTemplateAssignment = async (
  entityId: number,
): Promise<TemplateAssignmentResponse> => {
  const response = await axios.get<TemplateAssignmentResponse>(
    `${apiUrl}/cleaning/template/assignment/${entityId}`,
  );
  return response.data;
};

export const saveTemplateAssignment = async (
  assignments: TemplateAssignmentRequest[],
): Promise<void> => {
  await axios.post(`${apiUrl}/cleaning/template/assignment`, assignments);
};

// Template Test
export const testTemplate = async (
  templateId: number,
  testValues: string[],
): Promise<TemplateTestResponse> => {
  const response = await axios.post<TemplateTestResponse>(
    `${apiUrl}/cleaning/template/${templateId}/test`,
    testValues,
  );
  return response.data;
};
